import { MAX_TERMINAL_CHARS, MAX_TERMINAL_LINES } from '@engineer-os/engine';

/**
 * The newest part of the terminal, as text, for the mentor.
 *
 * What the learner typed and what it printed is the most direct evidence
 * there is of what they are doing — the `node --test` they just ran, the
 * error it gave, the `git status` they read — and none of it is anywhere but
 * the screen: the page records that a command finished, never what it was
 * (web/src/api.ts, `commandDone`). So it is read off the screen, at the moment
 * a question is asked, and nowhere else.
 *
 * Two halves. `bufferTail` reads an xterm buffer and is pure, so it can be
 * tested without a terminal. The registry lets the pane that owns the
 * terminal say "this is how to read me" and `useSession` ask at question time,
 * without either importing the other: the terminal is built inside a React
 * effect, and the hook that asks the question is not its parent.
 */

/** The part of xterm's `IBufferLine` this reads. */
export interface TailLine {
  readonly isWrapped: boolean;
  translateToString(trimRight?: boolean): string;
}

/** The part of xterm's `IBuffer` this reads. */
export interface TailBuffer {
  readonly length: number;
  getLine(y: number): TailLine | undefined;
}

/**
 * The newest lines of a buffer, oldest first, within both caps.
 *
 * Read from the end, because the newest lines are the ones kept and the
 * scrollback holds five thousand. A line the terminal wrapped across several
 * rows is joined back into one, which is what the program printed: the row
 * that wrapped was full to the edge, so it is read untrimmed — a space at the
 * wrap is part of the text — and only the last row of each line has its
 * trailing padding cut. The empty rows under the prompt are not lines and do
 * not spend the budget.
 */
export function bufferTail(buffer: TailBuffer, maxLines = MAX_TERMINAL_LINES, maxChars = MAX_TERMINAL_CHARS): string {
  const lines: string[] = [];
  let chars = 0;
  // Rows of the line being assembled, last row first.
  let rows: string[] = [];
  const finish = (): boolean => {
    const text = rows.reverse().join('');
    rows = [];
    if (lines.length === 0 && text.trim() === '') return false;
    lines.push(text);
    chars += text.length + 1;
    return lines.length >= maxLines || chars >= maxChars;
  };
  for (let y = buffer.length - 1; y >= 0; y -= 1) {
    const row = buffer.getLine(y);
    if (!row) continue;
    rows.push(row.translateToString(rows.length === 0));
    // A wrapped row continues the one above it: keep climbing.
    if (row.isWrapped) continue;
    if (finish()) break;
  }
  // The top of the scrollback can be the middle of a line whose first rows
  // have already scrolled away. What is left of it is still text.
  if (rows.length > 0 && lines.length < maxLines) finish();
  let text = lines.reverse().join('\n');
  if (text.length > maxChars) {
    text = text.slice(text.length - maxChars);
    const nl = text.indexOf('\n');
    if (nl >= 0 && nl < text.length - 1) text = text.slice(nl + 1);
  }
  return text;
}

/* ── Who can be read ─────────────────────────────────────────────────────── */

const readers = new Map<string, () => string>();

/**
 * Say how to read the terminal attached to this session.
 *
 * The returned function unregisters, and only if this is still the reader on
 * record: React's strict mode builds the pane twice, and the first one's
 * teardown must not take away the second one's registration.
 */
export function registerTerminalReader(sessionId: string, read: () => string): () => void {
  readers.set(sessionId, read);
  return () => {
    if (readers.get(sessionId) === read) readers.delete(sessionId);
  };
}

/**
 * The terminal's newest text for this session, or undefined when there is no
 * terminal to read or nothing on it. Never throws: a terminal that cannot be
 * read is a question asked with one fewer piece of evidence.
 */
export function readTerminal(sessionId: string): string | undefined {
  const read = readers.get(sessionId);
  if (!read) return undefined;
  try {
    const text = read();
    return text.trim() === '' ? undefined : text;
  } catch {
    return undefined;
  }
}
